import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchRoles, fetchUsers } from "../api/api";

const PAGE_SIZE = 25;

function paymentStatusLabel(customer) {
  if (customer.payment_overdue) {
    return { text: `⚠️ Overdue (was due ${new Date(customer.next_payment_due_date).toLocaleDateString()})`, color: "#dc2626" };
  }
  if (customer.next_payment_due_date) {
    return { text: `Next due ${new Date(customer.next_payment_due_date).toLocaleDateString()}`, color: "#666" };
  }
  return { text: "No pending payment", color: "#2e7d32" };
}

function errorMessage(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  if (status === 409) return detail || "A user with this mobile number or email already exists.";
  if (status === 404) return detail || "Selected role was not found.";
  if (status === 422) return "Please check the form — some fields are invalid or missing.";
  return "Something went wrong. Please try again.";
}

function Customer() {

  const [customerRoleId, setCustomerRoleId] = useState(null);
  const [roleLoadError, setRoleLoadError] = useState("");

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState("");
  const [search, setSearch] = useState("");

  const [viewMode, setViewMode] = useState("card"); // "card" | "table"
  const [page, setPage] = useState(1);


  // =========================
  // LOAD "customer" ROLE
  // =========================

  useEffect(() => {
    async function loadCustomerRole() {
      try {
        const roles = await fetchRoles();
        const customerRole = roles.find((r) => r.name.toLowerCase() === "customer");
        if (customerRole) {
          setCustomerRoleId(customerRole.id);
        } else {
          setRoleLoadError('No "customer" role exists yet.');
        }
      } catch (err) {
        setRoleLoadError("Could not load roles. Please refresh and try again.");
      }
    }
    loadCustomerRole();
  }, []);


  // =========================
  // LOAD CUSTOMER DIRECTORY
  // =========================

  const loadCustomers = async (roleId, q) => {
    setCustomersLoading(true);
    setCustomersError("");
    try {
      const data = await fetchUsers({ roleId, q: q || undefined });
      setCustomers(data);
      setPage(1);
    } catch (err) {
      setCustomersError(errorMessage(err));
    } finally {
      setCustomersLoading(false);
    }
  };

  useEffect(() => {
    if (customerRoleId) {
      loadCustomers(customerRoleId);
    }
  }, [customerRoleId]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (customerRoleId) {
      loadCustomers(customerRoleId, search);
    }
  };

  const totalPages = Math.max(1, Math.ceil(customers.length / PAGE_SIZE));
  const pagedCustomers = customers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);


  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="main-content">

        <h1>Customers</h1>

        {roleLoadError && (
          <p className="login-error" style={{ color: "red" }}>
            {roleLoadError}
          </p>
        )}

        {/* Customers and their plots come from Zoho only — no import or
            manual create here. See Zoho Integration for the sync. */}

        {/* CUSTOMER DIRECTORY */}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginBottom: "15px", flexWrap: "wrap" }}>

          <form onSubmit={handleSearch} style={{ display: "flex", gap: "10px", flex: 1, minWidth: "260px" }}>
            <input
              type="text"
              placeholder="Search by name, mobile, or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-bar"
            />
            <button type="submit" disabled={!customerRoleId}>
              Search
            </button>
          </form>

          <div style={{ display: "flex", gap: "0", border: "1px solid #ccc", borderRadius: "8px", overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setViewMode("card")}
              style={{
                width: "auto",
                padding: "8px 16px",
                borderRadius: 0,
                background: viewMode === "card" ? "#2e7d32" : "white",
                color: viewMode === "card" ? "white" : "#333",
              }}
            >
              Cards
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              style={{
                width: "auto",
                padding: "8px 16px",
                borderRadius: 0,
                background: viewMode === "table" ? "#2e7d32" : "white",
                color: viewMode === "table" ? "white" : "#333",
              }}
            >
              Table
            </button>
          </div>

        </div>

        {customersError && (
          <p className="login-error" style={{ color: "red" }}>
            {customersError}
          </p>
        )}

        {customersLoading && <p>Loading customers…</p>}

        {!customersLoading && customers.length === 0 && (
          <p>No customers found.</p>
        )}

        {!customersLoading && customers.length > 0 && (
          <p style={{ color: "#666" }}>
            Showing {pagedCustomers.length} of {customers.length} customer(s)
          </p>
        )}

        {!customersLoading && customers.length > 0 && viewMode === "card" && (
          <div className="dashboard-grid">

            {pagedCustomers.map((customer) => (

              <div className="card" key={customer.id}>

                <h3>{customer.full_name}</h3>

                <p>📞 {customer.mobile}</p>

                {customer.email && <p>📧 {customer.email}</p>}

                <p>
                  {/* This is the account's login status (User.is_active) — not
                      to be confused with a plot's maintenance status below,
                      which can separately be named "Active" too. */}
                  Account: {customer.is_active ? "✅ Active" : "🚫 Inactive"}
                </p>

                <p style={{ color: customer.self_registered ? "#166534" : "#92400e" }}>
                  {customer.self_registered ? "📱 Registered in app" : "☁️ Zoho only — not yet registered"}
                </p>

                {customer.all_plots?.length > 0 ? (
                  <div>
                    <p style={{ marginBottom: "4px" }}>
                      🏷️ Plot{customer.all_plots.length > 1 ? "s" : ""} & Maintenance:
                    </p>
                    {customer.all_plots.map((p) => (
                      <p key={p.plot_number} style={{ margin: "0 0 2px 16px", fontSize: "13px" }}>
                        <strong>{p.plot_number}</strong>: {p.maintenance_type || "No maintenance set"}
                        {p.maintenance_status ? ` (${p.maintenance_status})` : ""}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p>🏷️ Plot: —</p>
                )}

                <p style={{ color: paymentStatusLabel(customer).color, fontWeight: 600 }}>
                  💳 {paymentStatusLabel(customer).text}
                </p>

              </div>

            ))}

          </div>
        )}

        {!customersLoading && customers.length > 0 && viewMode === "table" && (
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>
            <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Account</th>
                  <th>Registration</th>
                  <th>Plot</th>
                  <th>Maintenance</th>
                  <th>Payment</th>
                </tr>
              </thead>
              <tbody>
                {pagedCustomers.map((customer) => (
                  <tr key={customer.id}>
                    <td><b>{customer.full_name}</b></td>
                    <td>{customer.mobile}</td>
                    <td>{customer.email || "—"}</td>
                    <td>{customer.is_active ? "✅ Active" : "🚫 Inactive"}</td>
                    <td style={{ color: customer.self_registered ? "#166534" : "#92400e" }}>
                      {customer.self_registered ? "Registered" : "Zoho only"}
                    </td>
                    <td>{customer.all_plots?.length > 0 ? customer.all_plots.map((p) => p.plot_number).join(", ") : "—"}</td>
                    <td>
                      {customer.all_plots?.length > 0 ? (
                        customer.all_plots.map((p) => (
                          <div key={p.plot_number} style={{ fontSize: "13px" }}>
                            <strong>{p.plot_number}</strong>: {p.maintenance_type || "—"}
                            {p.maintenance_status ? ` (${p.maintenance_status})` : ""}
                          </div>
                        ))
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ color: paymentStatusLabel(customer).color, fontWeight: 600 }}>
                      {paymentStatusLabel(customer).text}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!customersLoading && customers.length > 0 && totalPages > 1 && (
          <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: "15px" }}>
            <button className="register-btn" style={{ width: "auto", padding: "6px 14px" }} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button className="register-btn" style={{ width: "auto", padding: "6px 14px" }} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              Next
            </button>
          </div>
        )}

      </div>

    </div>

  );

}

export default Customer;
